import { useTranslation } from 'next-i18next'

import Avatar2D from '~/components/Avatar2D'
import ROUTES from '~/shared/constants/routes.const'
import getUserTitle from '~/components/UserLevel/UserLevel.utils'
import useCalculateLevel from '~/hooks/useCalculateLevel'
import useCurrentUser from '~/hooks/useCurrentUser'
import { Icon } from '~/components'
import { useGetRewardsQuery } from '~/redux/apis/reward-api'
import { useAppDispatch } from '~/redux/hooks'
import { getLeagueBadgeSrcKey } from '~/statistics/utils/getLeagueBadgeSrckey'
import { useGetCurrentLeagueQuery } from '~/Leaderboard/apis/leaderboard-api'
import { IUser } from '~/types/user-info-types'
import useCurrentFamily from '~/familyAccount/hooks/useCurrentFamily'
import { FamilyAccountsColorClasses } from '~/familyAccount/familyAccounts.constants'
import { setShowNicknameOverlay } from '~/statistics/redux/statistics.slice'
import { DEFAULT_AVATAR_URL } from '~/shared/constants/assets.const'
import { getReadyPlayerMeAvatarUrl, isReadyPlayerMeAvatarUrl } from '~/dashboard/hooks/util'

export default function UserProfile({ familyMember }: { familyMember?: IUser }): React.ReactElement {
  const { t } = useTranslation(['homebase', 'statistics'])
  const dispatch = useAppDispatch()
  const currentUser = useCurrentUser()
  const user = familyMember ?? currentUser
  const { familyUserData } = useCurrentFamily()
  const memberData = familyUserData?.find((member) => member.userId === user?.id)
  const profileColor = memberData ? FamilyAccountsColorClasses[memberData.profileColor] : 'bg-gradient-pink-purple'

  const rewards = useGetRewardsQuery(familyMember ? { userId: familyMember.id } : undefined)
  const points = rewards?.data?.xp ? rewards.data.xp : 0
  const userLevelData = useCalculateLevel(points)
  const userTitleKey = getUserTitle(userLevelData.lvl)
  const userTitle = t(`homebase:${userTitleKey}`)

  // the following statements will be replaced by the hook which is created by @nazmul bhai.
  // hook needs some modification to use it globally.
  const lookingFor = 'under-13/glb/'
  const isUnder13 = user?.avatarUrl?.indexOf(lookingFor) !== -1
  const avatarUrl = user?.avatarUrl?.replace('.glb', '.png')
  const isReadyPlayer = avatarUrl && isReadyPlayerMeAvatarUrl(avatarUrl)

  const avatarPngUrl = isUnder13 || !isReadyPlayer ? user?.avatarPngUrl : avatarUrl

  const isS3Bucket = !!(isUnder13 || !isReadyPlayer)
  const leagueRank = useGetCurrentLeagueQuery()
  const LeagueBadge = getLeagueBadgeSrcKey(leagueRank)

  const url = memberData ? avatarPngUrl || DEFAULT_AVATAR_URL : avatarUrl
  const avatarImageSrc = url && isReadyPlayerMeAvatarUrl(url) ? getReadyPlayerMeAvatarUrl(url) : url

  return (
    <div className="space-y-1.5">
      <div className="w-full flex flex-col items-center space-y-2 mt-2">
        <Avatar2D
          avatarPngUrl={avatarImageSrc}
          btnHref={`${ROUTES.AVATAR}?from=statistics`}
          createAvatarButtonSize="xx-small"
          currentLeague={LeagueBadge?.currentLeague}
          dimension={80}
          imgInnerClassName={profileColor}
          isS3Bucket={isS3Bucket}
          showCreateAvatarButton={!familyMember}
        />
      </div>

      {user?.nickname ? (
        <p className="mt-2 text-md break-words">{user.nickname}</p>
      ) : (
        <button
          className="mx-auto flex justify-center items-center font-semibold text-center text-md"
          onClick={() => dispatch(setShowNicknameOverlay(true))}
        >
          <p>{t('statistics:create-nickname')}</p>
          <Icon className="pl-4" customImageSrc="misc/edit-input-field.svg" />
        </button>
      )}
      <p className="text-xs font-light leading-3">{userTitle}</p>
    </div>
  )
}
